import type { Frame, UnitKind } from "@empire/core-sim";
import { STRIDE } from "./presentation.js";

export type Facing = "NE" | "SE" | "SW" | "NW";
interface Step {
  readonly index: number;
  readonly x: number;
  readonly y: number;
  readonly distance: number;
  readonly facing: Facing;
}
export type MotionTracks = ReadonlyMap<number, readonly Step[]>;
const cache = new WeakMap<readonly Frame[], MotionTracks>();

export function facingFor(dx: number, dy: number): Facing {
  return Math.abs(dx) >= Math.abs(dy) ? (dx > 0 ? "SE" : "NW") : dy > 0 ? "SW" : "NE";
}

/** Carry supplies, pause to work, and return. Decorative; never drives a queue. */
export function worksiteMotion(seconds: number, offset = 0) {
  const time = (((seconds + offset) % 8) + 8) % 8;
  const forward = time < 4;
  const leg = forward ? time : time - 4;
  const working = leg >= 3;
  const travel = Math.min(1, leg / 3);
  return {
    along: forward ? travel : 1 - travel,
    facing: (forward ? "SE" : "NW") as Facing,
    frame: working ? 0 : Math.floor(travel * 12) % 6,
    working,
    strike: working ? Math.max(0, Math.sin((leg - 3) * Math.PI * 4)) : 0,
  };
}

/** Presentation only. Build once per immutable replay; only recorded, visible positions enter it. */
export function motionTracks(frames: readonly Frame[]): MotionTracks {
  const known = cache.get(frames);
  if (known) return known;
  const tracks = new Map<number, Step[]>();
  frames.forEach((frame, index) => {
    for (let k = 0; k + 2 < frame.units.length; k += 3) {
      const id = frame.units[k]!;
      const x = frame.units[k + 1]!;
      const y = frame.units[k + 2]!;
      const track = tracks.get(id) ?? [];
      const last = track[track.length - 1];
      const continuous = last?.index === index - 1;
      const dx = continuous ? x - last.x : 0;
      const dy = continuous ? y - last.y : 0;
      const travel = Math.hypot(dx, dy);
      track.push({
        index,
        x,
        y,
        distance: (last?.distance ?? 0) + travel,
        facing: travel > 0 ? facingFor(dx, dy) : (last?.facing ?? "SW"),
      });
      tracks.set(id, track);
    }
  });
  cache.set(frames, tracks);
  return tracks;
}

/** Same moment, same pose, even after rewinding. Slowed/stationary troops cannot skate in place. */
export function motionAt(
  tracks: MotionTracks,
  id: number,
  position: number,
  kind: UnitKind = "militia"
): {
  facing: Facing;
  frame: number;
  moving: boolean;
  /** Continuous walk cycle, driven by travelled distance rather than a separate clock. */
  phase: number;
  /** Small bank into a corner; never changes the recorded position. */
  turn: number;
} {
  const track = tracks.get(id);
  if (!track?.length) return { facing: "SW", frame: 0, moving: false, phase: 0, turn: 0 };
  let lo = 0;
  let hi = track.length - 1;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (track[mid]!.index <= position) lo = mid;
    else hi = mid - 1;
  }
  const a = track[lo]!;
  const b = track[lo + 1];
  const moving = !!b && b.index === a.index + 1 && b.distance > a.distance && position >= a.index;
  const distance =
    a.distance + (moving ? (b.distance - a.distance) * Math.min(1, position - a.index) : 0);
  const cycle = distance / STRIDE[kind] + (id * 0.73) / 6;
  const phase = (cycle % 1) * Math.PI * 2;
  // Ease the weight into the new direction without smoothing the actual route
  // across a blocked tile. Visibility gaps and stops cannot initiate a turn.
  const previous = track[lo - 1];
  const continuous = previous?.index === a.index - 1 && a.distance > previous.distance;
  const directions: readonly Facing[] = ["NE", "SE", "SW", "NW"];
  const corner =
    moving && continuous
      ? (directions.indexOf(b.facing) - directions.indexOf(a.facing) + 4) % 4
      : 0;
  const turn =
    corner === 1 || corner === 3
      ? Math.sin(Math.min(1, (position - a.index) / 0.85) * Math.PI) * (corner === 1 ? 1 : -1)
      : 0;
  return {
    facing: moving ? b.facing : a.facing,
    frame: Math.floor(cycle * 6) % 6,
    moving,
    phase,
    turn,
  };
}
